import React, { useLayoutEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import Link from '@components/shared/Link';
import cx from 'classnames';
import _ from '@/utils/lodash';
import { getPagePath } from '@/utils/pagePaths';
import { cssLength, headerModuleStyleSheet, moduleBox } from '@/theme/header-modules';
import {
    buildBrandLogoStyle,
    fitWordmarkToWidth,
    logoFilterStyle,
    resolveBrandFilter,
    resolveLogoHref,
    resolveLogoSrc,
    wordmarkHasManualBreaks,
} from '@/theme/logo';
import {
    HEADER_BLOCK_TYPES,
    ensureHeaderBlocks,
    headerBlockClassName,
    headerBlockDeviceChanges,
    headerBlockProps,
} from '@/theme/header-blocks';
import FilteredMark from '@components/shared/FilteredMark';
import GlyphPreview from '@components/library/GlyphPreview';
import { resolveGlyph } from '@/library';
import { useAppContext } from '@/context/AppContext';
import { squishScale } from '@/theme/squish';
import useChromeLayer from '@/hooks/useChromeLayer';

const closeImageLink = ({ pages, params, pagesObj }) => {
    try {
        let page = null;
        if (params.page && pagesObj) {
            if (params.page.includes('/')) {
                const { findPageByPath } = require('@/utils/pagePaths');
                page = findPageByPath(params.page, pagesObj);
            } else {
                const { findPageBySlug } = require('@/utils/pagePaths');
                const result = findPageBySlug(params.page, pagesObj);
                page = result?.page;
            }
        }
        if (!page) {
            page = _.find(pages, (p) => (p.slug || p.url) === params.page);
        }
        const { exitUrl } = page || {};
        if (exitUrl) return exitUrl;
        if (page && pagesObj) {
            const fullPath = getPagePath(page, pagesObj);
            return fullPath ? `/${fullPath}` : '/';
        }
        return params.page ? `/${params.page}` : '/';
    } catch (e) {
        return params.page ? `/${params.page}` : '/';
    }
};

const PageLink = ({ highlight, image, name, shownImage, fullPath, isPublic, gapVar }) => {
    if (!isPublic) return null;

    const isHighlighted = highlight === fullPath;
    const gapStyle = {
        marginLeft: `var(${gapVar || '--header-nav-gap'}, 1em)`,
        marginRight: `var(${gapVar || '--header-nav-gap'}, 1em)`,
    };

    // Hover colour / underline come from the Menu block's --hb-hover-* vars
    // (see headerModuleStyleSheet). Keep no-underline here so global a:hover
    // does not force a line before the block token applies.
    return (highlight === fullPath && image) ? (
        <Link
            key={fullPath}
            className={cx(
                'inline-block align-top transition-colors duration-200 no-underline',
                { 'highlight text-gray-500': isHighlighted },
            )}
            style={gapStyle}
            to={`/${fullPath}/${image}.img`}
        >
            {name}
        </Link>
    ) : (
        <Link
            key={fullPath}
            className={cx(
                'inline-block align-top transition-colors duration-200 no-underline',
                { 'highlight text-gray-500': isHighlighted },
            )}
            style={gapStyle}
            to={`/${fullPath}`}
        >
            {name}
        </Link>
    );
};

PageLink.propTypes = {
    highlight: PropTypes.string,
    image: PropTypes.string,
    name: PropTypes.string,
    shownImage: PropTypes.string,
    fullPath: PropTypes.string,
    isPublic: PropTypes.bool,
    gapVar: PropTypes.string,
};

/** The block with its props for the current screen size. */
function useDeviceBlock(block) {
    const layer = useChromeLayer();
    return useMemo(
        () => (block?.deviceProps
            ? { ...block, props: headerBlockProps(block, layer), deviceChanges: headerBlockDeviceChanges(block, layer) }
            : block),
        [block, layer],
    );
}

function stripTags(html) {
    return String(html || '').replace(/<[^>]*>/g, '').trim();
}

function BrandBlock({ block: sourceBlock, theme, properties, pagesObj }) {
    const block = useDeviceBlock(sourceBlock);
    const siteLogo = theme?.brand?.logo;
    const props = block.props || {};
    const mode = props.mode || siteLogo?.mode || 'mask';
    const logoStyle = buildBrandLogoStyle(props, siteLogo);
    const filter = resolveBrandFilter(block, siteLogo);
    const logoSrc = resolveLogoSrc({
        ...(siteLogo || {}),
        mode,
        fileId: props.fileId != null && props.fileId !== '' ? props.fileId : (siteLogo?.fileId ?? null),
    }, 'page');
    const plainText = props.text || stripTags(props.textHtml) || properties?.header_title || '';
    const logoLabel = mode === 'text'
        ? plainText
        : (props.alt || properties?.header_title || '');
    const moduleRef = useRef(null);
    const fillRef = useRef(null);

    const logoLinkPageId = resolveLogoHref(block, siteLogo, properties);
    let logoLinkPath = '/';
    if (logoLinkPageId && pagesObj?.[logoLinkPageId]) {
        const logoPage = pagesObj[logoLinkPageId];
        const fullPath = getPagePath(logoPage, pagesObj);
        logoLinkPath = fullPath ? `/${fullPath}` : '/';
    }

    const box = moduleBox({ [block.id]: block.boxes }, block.id, 'desktop');
    const isText = mode === 'text';
    // Text wordmarks default to hug, but Placement → Fixed width must still apply.
    const hugging = box.fit === 'hug'
        || (box.fit !== 'fixed' && box.w === 'auto' && box.h === 'auto');
    const textHtml = props.textHtml && props.textHtml.includes('<')
        ? props.textHtml
        : null;
    const hasBreaks = isText && wordmarkHasManualBreaks(props.textHtml || textHtml, plainText);
    // Fixed width + single line: scale type so the mark spans the box edge-to-edge.
    const fitWidth = isText && !hugging && !hasBreaks && Boolean(box.w && box.w !== 'auto');
    const hoverOpacity = props.hoverOpacity == null ? 0.8 : Number(props.hoverOpacity);
    const hoverDecoration = props.hoverUnderline === true
        ? 'underline'
        : props.hoverUnderline === false
            ? 'none'
            : undefined;
    const linkStyle = {
        ...(isText ? {
            ...logoStyle,
            ...logoFilterStyle(filter),
            whiteSpace: hasBreaks ? 'pre-line' : 'nowrap',
            fontWeight: props.weight || undefined,
            fontStyle: props.style === 'italic' ? 'italic' : undefined,
            letterSpacing: props.tracking != null ? `${props.tracking}em` : undefined,
            textTransform: props.casing ? (props.casing === 'none' ? 'none' : props.casing) : undefined,
            scale: squishScale(props.squish),
        } : {}),
        '--hb-hover-opacity': String(Number.isFinite(hoverOpacity) ? hoverOpacity : 0.8),
        ...(props.hoverColor ? { '--hb-hover-color': props.hoverColor } : {}),
        ...(hoverDecoration ? { '--hb-hover-decoration': hoverDecoration } : {}),
    };

    useLayoutEffect(() => {
        const moduleEl = moduleRef.current;
        const fillEl = fillRef.current;
        if (!fitWidth || !moduleEl || !fillEl) {
            if (fillEl) {
                fillEl.style.fontSize = '';
                delete fillEl.dataset.fitWidthPx;
            }
            if (moduleEl) {
                moduleEl.style.fontSize = '';
                moduleEl.style.height = '';
                delete moduleEl.dataset.wordmarkReady;
            }
            return undefined;
        }

        let cancelled = false;
        let raf = 0;
        const fitNow = () => {
            fitWordmarkToWidth(moduleEl, fillEl);
            moduleEl.dataset.wordmarkReady = '1';
        };
        const family = (getComputedStyle(fillEl).fontFamily || '').split(',')[0].trim().replace(/^["']|["']$/g, '');
        const weight = props.weight || 400;
        const fontStyle = props.style === 'italic' ? 'italic' : 'normal';
        const spec = family ? `${fontStyle} ${weight} 16px "${family}"` : '';
        const fonts = typeof document !== 'undefined' ? document.fonts : null;
        if (!fonts || !spec || fonts.check(spec)) {
            // Font is already here: size before paint so the logo never eases in.
            fitNow();
        } else {
            delete moduleEl.dataset.wordmarkReady;
            fonts.load(spec).then(() => {
                if (!cancelled) fitNow();
            }).catch(() => {
                if (!cancelled) fitNow();
            });
        }

        const runLater = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
                if (!cancelled) fitWordmarkToWidth(moduleEl, fillEl);
            });
        };
        const ro = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(runLater)
            : null;
        ro?.observe(moduleEl);
        fonts?.addEventListener?.('loadingdone', runLater);
        const root = typeof document !== 'undefined' ? document.documentElement : null;
        const mo = root && typeof MutationObserver !== 'undefined'
            ? new MutationObserver(() => {
                delete fillEl.dataset.fitWidthPx;
                fitNow();
            })
            : null;
        mo?.observe(root, { attributes: true, attributeFilter: ['data-header-bp'] });
        return () => {
            cancelled = true;
            cancelAnimationFrame(raf);
            ro?.disconnect();
            mo?.disconnect();
            fonts?.removeEventListener?.('loadingdone', runLater);
        };
    }, [fitWidth, plainText, textHtml, props.faceId, props.textHtml, props.weight, props.style, box.w, box.h]);

    return (
        <span
            ref={moduleRef}
            className={`${headerBlockClassName(block)} inline-block`}
            data-theme-path="header.logoWidth"
            data-header-module={block.id}
            data-header-block={block.id}
            data-header-block-type={HEADER_BLOCK_TYPES.BRAND}
            data-fit={hugging ? 'hug' : 'fixed'}
            data-wordmark-fit={fitWidth ? 'width' : undefined}
            data-lock-ratio={hugging || box.lockRatio === false ? '0' : '1'}
        >
            <Link
                aria-label={isText ? undefined : (logoLabel || undefined)}
                className={cx(
                    'logo-img relative block w-full opacity-100',
                    fitWidth ? 'h-auto' : 'h-full',
                )}
                style={linkStyle}
                to={logoLinkPath}
            >
                {!isText && (
                    <FilteredMark
                        src={logoSrc}
                        tint={mode !== 'image'}
                        filter={filter}
                        maskPosition="left center"
                        className="pointer-events-none absolute inset-0"
                    />
                )}
                {isText && textHtml ? (
                    <span
                        ref={fillRef}
                        className="header-module-fill header-wordmark"
                        dangerouslySetInnerHTML={{ __html: textHtml }}
                    />
                ) : isText ? (
                    <span ref={fillRef} className="header-module-fill">{logoLabel}</span>
                ) : null}
            </Link>
        </span>
    );
}

BrandBlock.propTypes = {
    block: PropTypes.object.isRequired,
    theme: PropTypes.object,
    properties: PropTypes.object,
    pagesObj: PropTypes.object,
};

function MenuBlock({ block: sourceBlock, pages, params, pagesObj, theme }) {
    const block = useDeviceBlock(sourceBlock);
    const props = block.props || {};
    const getSlug = (page) => page?.slug || page?.url || '';
    const getParentId = (page) => page?.parentId || page?.parentUrl || null;

    // Header Menu follows the page's Menu toggle (isPublic). Nav (showInNav)
    // only affects the drawer / side menu.
    const navPages = pages.filter((page) => page.isPublic);
    const navSource = props.navSource || theme?.header?.navSource || 'all-public';
    const visibleNavPages = navSource === 'root-public'
        ? navPages.filter((page) => !getParentId(page))
        : navPages;

    const size = props.size ?? theme?.header?.navSize ?? 1;
    const tracking = props.tracking ?? theme?.header?.navTracking ?? 0.2;
    const casing = props.casing || theme?.header?.navCasing || 'uppercase';
    const gap = props.gap ?? theme?.header?.navGap ?? 1;
    const color = props.color || null;

    // No mode of its own: the CSS falls back to the site's --link-* underline.
    const underlineMode = ['always', 'hover', 'never'].includes(props.underlineMode)
        ? props.underlineMode
        : props.underline === true ? 'always' : null;
    const decoration = underlineMode ? (underlineMode === 'always' ? 'underline' : 'none') : undefined;
    const hoverDecoration = props.hoverUnderline === true
        ? 'underline'
        : props.hoverUnderline === false
            ? 'none'
            : underlineMode ? (underlineMode === 'never' ? 'none' : 'underline') : undefined;
    const style = {
        fontFamily: props.faceId
            ? `var(--font-face-${props.faceId}, var(--font-nav, "Sansumi", system-ui, sans-serif))`
            : 'var(--font-nav, "Sansumi", system-ui, sans-serif)',
        // The module stylesheet sets font-size from this; the editor preview
        // clears inline font sizes on header modules after every edit.
        '--header-block-nav-size': `${size}em`,
        fontWeight: props.weight || undefined,
        fontStyle: props.style === 'italic' ? 'italic' : undefined,
        letterSpacing: `${tracking}em`,
        '--menu-leading': props.lineHeight || undefined,
        textTransform: casing === 'none' ? 'none' : casing,
        scale: squishScale(props.squish),
        ...(color ? { color } : {}),
        '--header-block-nav-gap': `${gap}em`,
        // A custom property set to inherit would take the parent's (unset) value; currentColor keeps the link's.
        ...(props.hoverColor ? { '--hb-hover-color': props.hoverColor === 'inherit' ? 'currentColor' : props.hoverColor } : {}),
        ...(Number.isFinite(Number(props.hoverOpacity)) && props.hoverOpacity != null
            ? { '--hb-hover-opacity': String(props.hoverOpacity) } : {}),
        ...(props.hoverWeight ? { '--hb-hover-weight': String(props.hoverWeight) } : {}),
        ...(props.hoverStyle ? { '--hb-hover-style': props.hoverStyle } : {}),
        ...(hoverDecoration ? { '--hb-hover-decoration': hoverDecoration } : {}),
        ...(decoration ? { '--hb-decoration': decoration } : {}),
        ...(cssLength(props.underlineThickness, 'px') ? { '--hb-thickness': cssLength(props.underlineThickness, 'px') } : {}),
        ...(cssLength(props.underlineOffset, 'em') ? { '--hb-offset': cssLength(props.underlineOffset, 'em') } : {}),
    };

    const menuBox = moduleBox({ [block.id]: block.boxes }, block.id, 'desktop');
    const menuHug = menuBox.fit === 'hug' || (menuBox.w === 'auto' && menuBox.h === 'auto');

    return (
        <div
            className={`${headerBlockClassName(block)} menu font-sansumi align-middle`}
            style={style}
            data-fit={menuHug ? 'hug' : 'fixed'}
            data-theme-path="header.navSize"
            data-header-module={block.id}
            data-header-block={block.id}
            data-header-block-type={HEADER_BLOCK_TYPES.MENU}
            data-lock-ratio="0"
        >
            {visibleNavPages.map((page) => (
                <PageLink
                    key={getSlug(page) || page.id}
                    highlight={params.page && ''}
                    shownImage={params.image}
                    image={!page.html ? (page.gallery?.images?.[0]) : null}
                    name={page.name}
                    isPublic={page.isPublic}
                    fullPath={getPagePath(page, pagesObj)}
                    gapVar="--header-block-nav-gap"
                />
            ))}
        </div>
    );
}

MenuBlock.propTypes = {
    block: PropTypes.object.isRequired,
    pages: PropTypes.array,
    params: PropTypes.object,
    pagesObj: PropTypes.object,
    theme: PropTypes.object,
};

function MenuToggleBlock({ block: sourceBlock, theme, library }) {
    const { toggleMenu, showMenu } = useAppContext();
    const block = useDeviceBlock(sourceBlock);
    const props = block.props || {};
    const label = props.label || 'Menu';
    const box = moduleBox({ [block.id]: block.boxes }, block.id, 'desktop');
    const hugging = box.fit === 'hug' || (box.w === 'auto' && box.h === 'auto');
    const hoverOpacity = props.hoverOpacity == null ? 1 : Number(props.hoverOpacity);
    const style = {
        ...(props.color ? { color: props.color, '--hb-toggle-color': props.color } : {}),
        ...(Number.isFinite(hoverOpacity) ? { '--hb-hover-opacity': String(hoverOpacity) } : {}),
        ...(props.hoverColor ? { '--hb-hover-color': props.hoverColor } : {}),
    };
    const glyphId = showMenu ? props.closeGlyph : props.openGlyph;
    const glyph = glyphId ? resolveGlyph(theme?.icons?.set, glyphId, library) : null;

    return (
        <span
            className={`${headerBlockClassName(block)} header-menu-toggle inline-block`}
            style={style}
            data-theme-path="header.menuToggle"
            data-header-module={block.id}
            data-header-block={block.id}
            data-header-block-type={HEADER_BLOCK_TYPES.MENU_TOGGLE}
            data-custom-icon={glyph ? '1' : '0'}
            data-fit={hugging ? 'hug' : 'fixed'}
            data-lock-ratio="0"
        >
            <button
                type="button"
                className="block cursor-pointer border-0 bg-transparent p-0"
                aria-label={label}
                aria-expanded={Boolean(showMenu)}
                onClick={() => {
                    // Inspect/picker selects the block; don't open the drawer.
                    if (typeof document !== 'undefined'
                        && document.body.classList.contains('theme-inspect-active')) {
                        return;
                    }
                    toggleMenu();
                }}
            >
                {glyph ? (
                    <GlyphPreview glyph={glyph} size="1em" />
                ) : (
                    <span className="hamburger-icon" aria-hidden="true" />
                )}
            </button>
        </span>
    );
}

MenuToggleBlock.propTypes = {
    block: PropTypes.object.isRequired,
    theme: PropTypes.object,
    library: PropTypes.object,
};

function PageHeader({ pages, params, index, total, name, properties, content, theme }) {
    const pagesObj = content?.pages || {};
    const ensured = ensureHeaderBlocks(theme || {});
    const blocks = ensured.header?.blocks || [];
    const modules = ensured.header?.modules;

    const getSlug = (page) => page?.slug || page?.url || '';
    const currentPageExists = params.page && (
        pages.map((p) => getSlug(p)).indexOf(params.page) > -1
        || pages.map((p) => getSlug(p)).indexOf(params.page.split('/').pop()) > -1
    );

    if (!params.page || currentPageExists) {
        return (
            <header
                id="header"
                className="header-modules top-0 left-0 w-full z-[999] bg-transparent"
                style={{
                    paddingTop: 'var(--header-pad-y, 22px)',
                    paddingBottom: 'var(--header-pad-y, 22px)',
                    WebkitOverflowScrolling: 'touch',
                }}
                data-theme-path="header.padY"
                data-has-menu-toggle={blocks.some((b) => b.type === HEADER_BLOCK_TYPES.MENU_TOGGLE) ? '1' : '0'}
            >
                <style
                    dangerouslySetInnerHTML={{
                        __html: headerModuleStyleSheet(modules, { blocks }),
                    }}
                />
                <div className="header-column">
                    {blocks.map((block) => {
                        if (block.type === HEADER_BLOCK_TYPES.BRAND) {
                            return (
                                <BrandBlock
                                    key={block.id}
                                    block={block}
                                    theme={ensured}
                                    properties={properties}
                                    pagesObj={pagesObj}
                                />
                            );
                        }
                        if (block.type === HEADER_BLOCK_TYPES.MENU) {
                            return (
                                <MenuBlock
                                    key={block.id}
                                    block={block}
                                    pages={pages}
                                    params={params}
                                    pagesObj={pagesObj}
                                    theme={ensured}
                                />
                            );
                        }
                        if (block.type === HEADER_BLOCK_TYPES.MENU_TOGGLE) {
                            return <MenuToggleBlock key={block.id} block={block} theme={ensured} library={content?.library} />;
                        }
                        return null;
                    })}
                </div>
            </header>
        );
    }
    return null;
}

PageHeader.propTypes = {
    pages: PropTypes.array,
    params: PropTypes.object,
    index: PropTypes.number,
    total: PropTypes.number,
    name: PropTypes.string,
    properties: PropTypes.object,
    content: PropTypes.object,
    theme: PropTypes.object,
};

function ImageHeader({ pages, params, index, total, name, file, content }) {
    const pagesObj = content?.pages || {};
    return (
        <header
            id="header"
            className="slim fixed top-0 left-0 w-full overflow-x-hidden overflow-y-auto z-[999] bg-transparent"
            style={{
                paddingTop: '22px',
                WebkitOverflowScrolling: 'touch',
                WebkitBackfaceVisibility: 'hidden',
            }}
        >
            <nav className="nav-image page-column flex flex-row items-center justify-end">
                <h1 className="font-sansumi text-[1em] flex-grow ml-[2em] text-center tracking-[0.2em] uppercase font-normal">
                    {file?.description || '.'}
                </h1>
                <Link
                    className="self-end text-[2em] mr-[1em] transition-colors duration-200 hover:text-gray-400 hover:no-underline"
                    to={closeImageLink({ pages, params, index, pagesObj })}
                    aria-label="Close image viewer"
                >
                    <i className="icon-close" aria-hidden="true" />
                </Link>
            </nav>
        </header>
    );
}

ImageHeader.propTypes = {
    pages: PropTypes.array,
    params: PropTypes.object,
    index: PropTypes.number,
    total: PropTypes.number,
    name: PropTypes.string,
    file: PropTypes.object,
    content: PropTypes.object,
};

function Header(props) {
    return props.file === null ? <PageHeader {...props} /> : <ImageHeader {...props} />;
}

Header.propTypes = {
    file: PropTypes.object,
    pages: PropTypes.array,
    params: PropTypes.object,
    index: PropTypes.number,
    total: PropTypes.number,
    name: PropTypes.string,
    properties: PropTypes.object,
};

export default Header;
